import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Box, Spinner, VStack, Text } from '@chakra-ui/react';
import { Alert } from '@chakra-ui/react';
import { FiAlertCircle } from 'react-icons/fi';

interface AdminRouteProps {
  children: React.ReactNode;
}

const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box 
        minH="100vh" 
        display="flex" 
        alignItems="center" 
        justifyContent="center"
        bg="gray.50"
      >
        <VStack gap={4}>
          <Spinner size="xl" color="brand.500" />
          <Text>Loading...</Text>
        </VStack>
      </Box>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login page with return url
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!user?.is_superuser) {
    // User is authenticated but not an admin
    return (
      <Box 
        minH="100vh" 
        display="flex" 
        alignItems="center" 
        justifyContent="center"
        bg="gray.50"
        p={8}
      >
        <Alert.Root status="error" maxW="md" borderRadius="lg">
          <Alert.Indicator>
            <FiAlertCircle />
          </Alert.Indicator>
          <VStack align="start" gap={2}>
            <Alert.Title fontWeight="bold">Access Denied</Alert.Title>
            <Alert.Description>
              You don't have permission to access the admin panel. Please contact your administrator.
            </Alert.Description>
          </VStack>
        </Alert.Root>
      </Box>
    );
  }

  return <>{children}</>;
};

export default AdminRoute;
